import http from "@/Service/http-common";
import { getAuthToken } from "@/Service/authService";
import { uploadTelemetryService } from "@/utils/uploadTelemetry";
import { getAnalytics } from "../analytics";
import { UPLOAD_EVENT } from "./UploadEvents";
import { getClientEnvironment } from "../environment/ClientEnvironment";
import { getNetworkSnapshot } from "../network/NetworkTelemetry";

/**
 * A diagnostic snapshot is the frozen state of an upload at a moment of
 * interest (failure, stuck detection, or a manual admin capture). It is:
 *
 *   1. Sent to PostHog as a `upload_diagnostic_snapshot` event so it links
 *      to the session replay
 *   2. POSTed to the backend so admins can inspect it later without
 *      needing PostHog access
 *
 * Both writes are best-effort: a failed HTTP call must not disrupt the
 * upload (which may already be dead — this is the diagnostic capture path).
 *
 * The snapshot shape is intentionally flat + JSON-serialisable so it can go
 * straight into a Postgres JSONB column on the backend.
 */

const SNAPSHOT_ENDPOINT = (uploadId) =>
  `/api/v1/upload/${encodeURIComponent(uploadId)}/diagnostics-snapshot`;

// Bound how many timeline entries we ship — the backend column shouldn't grow
// unbounded, and the "last 100 events" is plenty for a support ticket.
const MAX_TIMELINE_ENTRIES_IN_SNAPSHOT = 100;

/**
 * Build a snapshot object without any side effects. Split from capture() so
 * tests can assert the shape without touching the network.
 *
 * @param {import('./UploadDiagnostics').UploadDiagnostics} diagnostics
 * @param {object} [options]
 * @param {"failure" | "stuck" | "manual"} [options.trigger]
 * @param {string | null} [options.reason]
 * @returns {object}
 */
export const buildDiagnosticSnapshot = (diagnostics, options = {}) => {
  const { trigger = "manual", reason = null } = options;
  const state = diagnostics.getState();
  const telemetryState = safeTelemetryState(diagnostics.uploadId);
  const env = getClientEnvironment();
  const network = getNetworkSnapshot();
  const analytics = getAnalytics();
  const timelineTail = diagnostics.timeline
    .toArray()
    .slice(-MAX_TIMELINE_ENTRIES_IN_SNAPSHOT);
  const latestErrors = diagnostics.timeline.tail(
    5,
    (entry) =>
      entry.event === UPLOAD_EVENT.UPLOAD_FAILED ||
      entry.event === UPLOAD_EVENT.CHUNK_FAILED ||
      entry.event === UPLOAD_EVENT.API_ERROR
  );

  return {
    uploadId: diagnostics.uploadId,
    capturedAt: Date.now(),
    trigger,
    reason,

    // Context stamped by announceUpload()
    context: diagnostics.getContext(),

    // State summary
    state: {
      uploaderState: state.uploaderState,
      health: state.health,
      paused: state.paused,
      bytesUploaded: state.bytesUploaded,
      totalBytes: state.totalBytes,
      chunkCount: state.chunkCount,
      currentChunkIndex: state.currentChunkIndex,
      retryCount: state.retryCount,
      lastProgressAt: state.lastProgressAt,
      lastActivityAt: state.lastActivityAt,
      terminalError: state.terminalError,
    },

    // Live throughput (from uploadTelemetryService)
    throughput: telemetryState
      ? {
          uploadedBytes: telemetryState.uploadedBytes,
          totalBytes: telemetryState.totalBytes,
          elapsedSeconds: telemetryState.elapsedTime,
          averageBps: telemetryState.averageBps,
          rollingBps: telemetryState.rollingBps,
          status: telemetryState.status,
        }
      : null,

    // Environment
    environment: env,

    // Network at capture time
    network,

    // Correlation (so support can jump to session replay / backend logs)
    correlation: {
      posthogSessionId: safeCall(() => analytics.getSessionId()),
      posthogSessionReplayUrl: safeCall(() => analytics.getSessionReplayUrl()),
      uploadSessionId: diagnostics.getContext().uploadSessionId || null,
    },

    // Recent history
    latestErrors,
    timeline: timelineTail,
  };
};

/**
 * Build + ship a snapshot. Fire-and-forget — never throws.
 *
 * @param {import('./UploadDiagnostics').UploadDiagnostics} diagnostics
 * @param {object} [options]  see buildDiagnosticSnapshot
 * @returns {Promise<object>} the snapshot (returned regardless of ship success)
 */
export const captureDiagnosticSnapshot = async (diagnostics, options = {}) => {
  const snapshot = buildDiagnosticSnapshot(diagnostics, options);

  // Analytics (fast, in-process)
  try {
    diagnostics.record(UPLOAD_EVENT.DIAGNOSTIC_SNAPSHOT, {
      trigger: snapshot.trigger,
      reason: snapshot.reason,
      health: snapshot.state.health,
    });
    getAnalytics().track(UPLOAD_EVENT.DIAGNOSTIC_SNAPSHOT, snapshot);
  } catch (_error) {
    /* analytics failure is non-fatal */
  }

  // Backend (slower, network)
  shipToBackend(snapshot).catch(() => {
    /* already logged inside shipToBackend */
  });

  return snapshot;
};

const shipToBackend = async (snapshot) => {
  let token;
  try {
    token = await getAuthToken();
  } catch (_error) {
    // No auth — can't ship. Snapshot still available in PostHog.
    return;
  }
  // Backend DTO (UploadDiagnosticSnapshotRequest) expects an envelope with
  // top-level `trigger`, `reason`, `posthogSessionId`, `posthogSessionUrl`
  // hoisted out of the snapshot blob so they can populate dedicated columns
  // for indexed lookups. The snapshot itself goes in the `snapshot` field.
  //
  // Previously we posted the raw snapshot at the top level — which meant the
  // backend saw `snapshot: undefined` and rejected with 400 "snapshot is
  // required". The bug lay dormant because the only triggers were stuck /
  // failed, which are rare in dev. Now that we also capture on started /
  // completed, this bug would surface on every upload.
  const request = {
    trigger: snapshot.trigger,
    reason: snapshot.reason,
    posthogSessionId: snapshot.correlation?.posthogSessionId || null,
    posthogSessionUrl: snapshot.correlation?.posthogSessionReplayUrl || null,
    snapshot,
  };
  try {
    await http.post(SNAPSHOT_ENDPOINT(snapshot.uploadId), request, {
      headers: {
        Authorization: `Bearer ${token}`,
        "X-Upload-Id": snapshot.uploadId,
      },
      timeout: 10_000,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn("[observability] snapshot ship failed", {
      uploadId: snapshot.uploadId,
      status: error?.response?.status,
      message: error?.message,
    });
  }
};

const safeCall = (fn) => {
  try {
    return fn();
  } catch (_error) {
    return null;
  }
};

const safeTelemetryState = (uploadId) => {
  try {
    return uploadTelemetryService.getState(uploadId);
  } catch (_error) {
    return null;
  }
};
